import { useCallback, useEffect, useState } from 'react';

import { useFormik } from 'formik';
import { useIntl } from 'react-intl';
import { object, string } from 'yup';

import isEmpty from 'lodash/isEmpty';

import Grid from '@mui/material/Grid2';
import Button from '@mui/material/Button';
import Tooltip from '@mui/material/Tooltip';
import IconButton from '@mui/material/IconButton';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import AddOutlinedIcon from '@mui/icons-material/AddOutlined';

import Dropdown from 'src/components/Dropdown';
import TextInput from 'src/components/TextInput';
import ConfirmModal from 'src/components/ConfirmModal';
import { PencilAdornment } from 'src/utils/components';
import {
  useCreateTutorial,
  useUpdateTutorial,
  useGetCohortOptions,
  useGetTutorialTypeOptions,
  type tutorialParamsType,
} from 'src/pages/SetupPage/api';

import messages from './messages';

type TutorialFormProps = {
  create: boolean;
  tutorial?: UCM.TutorialType;
};

export default function TutorialForm({ create, tutorial }: TutorialFormProps) {
  const { formatMessage } = useIntl();

  const [open, setOpen] = useState(false);
  const [validateOnChange, setValidateOnChange] = useState(false);

  const { data: cohortOptions } = useGetCohortOptions();
  const { data: tutorialTypeOptions } = useGetTutorialTypeOptions();
  const { mutate: updateTutorial, isPending: isUpdating } = useUpdateTutorial();
  const { mutate: createTutorial, isPending: isCreating } = useCreateTutorial();

  const initialValues = {
    code: '',
    titleEn: '',
    titleDe: '',
    typeCode: '',
    cohortCode: '',
    descriptionEn: '',
    descriptionDe: '',
  };

  const formik = useFormik<tutorialParamsType>({
    initialValues,
    validateOnChange,
    enableReinitialize: true,
    validationSchema: object().shape({
      titleEn: string().required(),
      titleDe: string().required(),
      code: string().required(),
      descriptionEn: string(),
      descriptionDe: string(),
      cohortCode: string(),
      typeCode: string(),
    }),
    onSubmit: () => {},
  });

  const { values, errors } = formik;

  // Set the values of the form when the tutorial is opened in edit mode
  useEffect(() => {
    if (tutorial && !create && open) {
      formik.setValues({
        code: tutorial.code,
        titleEn: tutorial.title.en,
        titleDe: tutorial.title.de,
        typeCode: tutorial.kind.code || '',
        cohortCode: tutorial.cohort.code || '',
        descriptionEn: tutorial.description.en || '',
        descriptionDe: tutorial.description.de || '',
      });
    }
  }, [open, tutorial, create]);

  const handleOpen = useCallback(() => {
    setOpen(true);
  }, []);

  const handleClose = useCallback(() => {
    setValidateOnChange(false);
    setOpen(false);
    formik.resetForm();
  }, []);

  const handleSubmit = useCallback(async () => {
    setValidateOnChange(true);

    const errors = await formik.validateForm();
    const isValid = isEmpty(errors);

    if (!isValid) return false;

    return new Promise<boolean>((resolve) => {
      const mutationOptions = {
        onError: () => resolve(false),
        onSuccess: () => resolve(true),
      };

      if (create) {
        return createTutorial(values, mutationOptions);
      }

      if (!create && tutorial) {
        return updateTutorial(
          { ...values, uuid: tutorial.uuid },
          mutationOptions,
        );
      }
    });
  }, [create, values, tutorial]);

  return (
    <ConfirmModal
      width="700px"
      onOpen={handleOpen}
      onClose={handleClose}
      onConfirm={handleSubmit}
      loading={isCreating || isUpdating}
      title={formatMessage(messages.title)}
      triggerButton={(handleOpen) =>
        create ? (
          <Button
            variant="outlined"
            onClick={handleOpen}
            startIcon={<AddOutlinedIcon color="primary" />}
          >
            {formatMessage(messages.createButton)}
          </Button>
        ) : (
          <Tooltip arrow title={formatMessage(messages.editTooltip)}>
            <IconButton onClick={handleOpen}>
              <EditOutlinedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )
      }
    >
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, md: 4 }}>
          <TextInput
            fullWidth
            name="code"
            value={values.code}
            error={!!errors.code}
            helperText={errors.code}
            EndIcon={PencilAdornment}
            onChange={formik.handleChange}
            label={formatMessage(messages.codeLabel)}
          />
        </Grid>

        <Grid size={{ xs: 12, md: 4 }}>
          <Dropdown
            fullWidth
            name="typeCode"
            options={tutorialTypeOptions}
            value={values.typeCode}
            error={!!errors.typeCode}
            helperText={errors.typeCode}
            onChange={formik.handleChange}
            label={formatMessage(messages.typeLabel)}
          />
        </Grid>

        <Grid size={{ xs: 12, md: 4 }}>
          <Dropdown
            fullWidth
            name="cohortCode"
            options={cohortOptions}
            value={values.cohortCode}
            error={!!errors.cohortCode}
            helperText={errors.cohortCode}
            onChange={formik.handleChange}
            label={formatMessage(messages.cohortLabel)}
          />
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <TextInput
            fullWidth
            name="titleEn"
            value={values.titleEn}
            error={!!errors.titleEn}
            EndIcon={PencilAdornment}
            onChange={formik.handleChange}
            label={formatMessage(messages.titleEnLabel)}
            helperText={errors.titleEn ?? formatMessage(messages.shownToUsers)}
          />
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <TextInput
            fullWidth
            name="titleDe"
            value={values.titleDe}
            error={!!errors.titleDe}
            EndIcon={PencilAdornment}
            onChange={formik.handleChange}
            label={formatMessage(messages.titleDeLabel)}
            helperText={errors.titleDe ?? formatMessage(messages.shownToUsers)}
          />
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <TextInput
            fullWidth
            name="descriptionEn"
            value={values.descriptionEn}
            error={!!errors.descriptionEn}
            EndIcon={PencilAdornment}
            onChange={formik.handleChange}
            label={formatMessage(messages.descriptionEnLabel)}
            helperText={
              errors.descriptionEn ?? formatMessage(messages.shownToUsers)
            }
          />
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <TextInput
            fullWidth
            name="descriptionDe"
            value={values.descriptionDe}
            error={!!errors.descriptionDe}
            EndIcon={PencilAdornment}
            onChange={formik.handleChange}
            label={formatMessage(messages.descriptionDeLabel)}
            helperText={
              errors.descriptionDe ?? formatMessage(messages.shownToUsers)
            }
          />
        </Grid>
      </Grid>
    </ConfirmModal>
  );
}
